import type { AxiosError, AxiosResponse } from 'axios';

import type { MFARegister } from '../components/pages/userAccount/hsbc/steps/defaultRegistration/step_3/passwordSetupForm';
import { removePreLoginRouterState } from '../router/routerUtils';
import { useZustand } from '../zustandStore';

import HttpRepository from './httpRepository';

export type Login = {
	username: string;
	password: string;
};

export type ForgotPassword = {
	username: string;
};

export type ResetPassword = {
	activationToken: string;
	password: string;
	userId: string;
};

export type Register = {
	firstName: string;
	lastName: string;
	username: string;
	companyName: string;
	opportunityId: string;
};

export type RegisterWithPhoneNumber = Omit<Register, 'opportunityId'> & {
	phoneNumber: string;
	phoneCountryCode: string;
	phoneNumberValue: string; // used in ui only
};

type Activate = {
	userId: string;
	activationToken: string;
};

export type ActivateUser = {
	userId: string;
	activationToken: string;
	companyName: string;
	username: string;
	firstName: string;
	lastName: string;
	password: string;
};

export type ActivateUserWithTemporaryPassword = ActivateUser & {
	temporaryPassword: string;
};

export type ActivateUserTemporaryPassword = Pick<ActivateUser, 'userId' | 'activationToken'> & {
	temporaryPassword: string;
};

type SamlResponse = {
	loginUrl: string;
	samlEnabled: boolean;
};

type ConfirmToken = {
	companyAccountId: string;
	companyName: string;
	firstName: string;
	lastName: string;
	username: string;
	temporaryPasswordRequired: boolean;
	// sent by be when user is already LOCKED for incorrect activation code  or OTP attempts
	activationCodeAttemptsExceeded: boolean | undefined; // HSBC ONLY
	passwordAttemptsExceeded?: boolean; //used in HSBC FOR LOCKED_ACCOUNT on too many wrong email/pass attempts
	phoneDetails?: {
		// HSBC only
		phoneCountryCode: string;
		phoneNumber: string;
		fullPhoneNumber: string;
	};
};

export type ChallengeName = 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA';

export type MFA_CODE = { code: string; challengeName: ChallengeName };

export type ApiError = {
	response?: {
		status: number;
		data?: {
			errorCode?: string;
			message?: string;
		};
	};
};

export const SessionRepository = {
	async logout() {
		const { resetZustandStore } = useZustand.getState();
		resetZustandStore();
		removePreLoginRouterState();
		const response = await HttpRepository.tradeshiftGo().post('/auth/logout', {});
		return response.data;
	},
	getStatus: async () => {
		const response: AxiosResponse<{ status: string }, AxiosError> =
			await HttpRepository.tradeshiftGo().get('/auth/status');
		return response.data;
	},
	async login<T>(data: Login) {
		const response: AxiosResponse<T, AxiosError> = await HttpRepository.tradeshiftGo().post('/auth/login', data);
		return response.data;
	},
	getSaml(email: string) {
		return HttpRepository.tradeshiftGo().get<SamlResponse>('/auth/saml?email=' + encodeURIComponent(email));
	},
	async register<Args, Response>(data: Args) {
		const response: AxiosResponse<Response, AxiosError> = await HttpRepository.tradeshiftGo().post(
			'/auth/register',
			data,
		);
		return response.data;
	},
	async forgotPassword(data: ForgotPassword) {
		return HttpRepository.tradeshiftGo().post('/auth/password/forgot', data);
	},

	async confirmToken(data?: Activate) {
		if (!data) {
			throw new Error('userId or activationToken is invalid');
		}
		const response: AxiosResponse<ConfirmToken, AxiosError> = await HttpRepository.tradeshiftGo().get(
			`/auth/users/${data.userId}/token/${data.activationToken}`,
		);
		return response.data;
	},

	async activateUser<ArgumentsType, ResponseData>(options: ArgumentsType): Promise<ResponseData> {
		const response: AxiosResponse<ResponseData, AxiosError> = await HttpRepository.tradeshiftGo().post(
			'/auth/activate',
			options,
		);

		return response.data;
	},

	// on register page
	async sendMfaCode(options: MFA_CODE) {
		const response: AxiosResponse<MFARegister, AxiosError> = await HttpRepository.tradeshiftGo().post(
			'/auth/mfa/register',
			options,
		);

		return response.data;
	},

	// send POST request so back-end sends sms code
	async askForSmsCode() {
		const response: AxiosResponse<MFARegister, AxiosError> =
			await HttpRepository.tradeshiftGo().post('/auth/phone/send');

		return response.data;
	},

	// send SMS code on user registration
	async sendSmsCode(options: MFA_CODE) {
		const response: AxiosResponse<MFARegister, AxiosError> = await HttpRepository.tradeshiftGo().post(
			'/auth/phone/verify',
			options,
		);

		return response.data;
	},

	// on login page or on register new company
	async sendMfaCodeChallenge(options: MFA_CODE) {
		return HttpRepository.tradeshiftGo().post('/auth/mfa/challenge', options);
	},

	async sendMfaCodeChallengeSkip() {
		return HttpRepository.tradeshiftGo().post('/auth/mfa/skip', '');
	},

	async requestActivationWithTemporaryPasswordQuery(options: ActivateUserTemporaryPassword) {
		return HttpRepository.tradeshiftGo()
			.post('auth/activate/password/verify', options)
			.then(response => response.data);
	},
	async resetPassword(data: ResetPassword) {
		return HttpRepository.tradeshiftGo().post('/auth/password/reset', data);
	},
	async reset2FA(userId: string) {
		const response: AxiosResponse<unknown, AxiosError> = await HttpRepository.tradeshiftGo().post(
			`/rest/users/${userId}/mfa/reset`,
		);
		return response.data;
	},
};
